// @core/stores/audittrail/AudittrailStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { audittrailApi } from '@core/api';
import type {
  AuditChainResponse,
  AuditRecord,
  AuditRecordResponse,
  ComplianceReportResponse,
  CreateDataSubjectAccessRequestRequest,
  CreateRetentionPolicyRequest,
  DataSubjectAccessRequestResponse,
  ExecuteRetentionPurgeRequest,
  ExecuteRetentionPurgeResponse,
  ExportAuditTrailRequest,
  ExportAuditTrailResponse,
  GenerateComplianceReportRequest,
  ListAuditRecordsResponse,
  ListComplianceReportsResponse,
  ListDataSubjectAccessRequestsResponse,
  ListRetentionPoliciesResponse,
  ProcessDataSubjectAccessRequestRequest,
  RecordAuditEventRequest,
  RetentionPolicyResponse,
  SearchAuditRecordsRequest,
  UpdateRetentionPolicyRequest,
  VerifyChainIntegrityRequest,
  VerifyChainIntegrityResponse,
} from '@core/types/audittrail';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AudittrailStore {
  // List state
  items: AuditRecord[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: AuditRecord | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, AuditRecord>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): AuditRecord | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: AuditRecord | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create data subject access request
   */
  async CreateDataSubjectAccessRequest(data: CreateDataSubjectAccessRequestRequest): Promise<DataSubjectAccessRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.CreateDataSubjectAccessRequest(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create retention policy
   */
  async CreateRetentionPolicy(data: CreateRetentionPolicyRequest): Promise<RetentionPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.CreateRetentionPolicy(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Execute retention purge
   */
  async ExecuteRetentionPurge(policy_id: string, data: ExecuteRetentionPurgeRequest): Promise<ExecuteRetentionPurgeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ExecuteRetentionPurge(policy_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Export audit trail
   */
  async ExportAuditTrail(data: ExportAuditTrailRequest): Promise<ExportAuditTrailResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ExportAuditTrail(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate compliance report
   */
  async GenerateComplianceReport(data: GenerateComplianceReportRequest): Promise<ComplianceReportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.GenerateComplianceReport(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get audit record by ID
   */
  async GetAuditRecord(record_id: string): Promise<AuditRecord> {
    // Check cache first
    const cached = this.cache.get(record_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await audittrailApi.GetAuditRecord(record_id);
      const item = response.record;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get chain status
   */
  async GetChainStatus(chain_id: string): Promise<AuditChainResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.GetChainStatus(chain_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get compliance report
   */
  async GetComplianceReport(report_id: string): Promise<ComplianceReportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.GetComplianceReport(report_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get data subject access request
   */
  async GetDataSubjectAccessRequest(request_id: string): Promise<DataSubjectAccessRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.GetDataSubjectAccessRequest(request_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get retention policy
   */
  async GetRetentionPolicy(policy_id: string): Promise<RetentionPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.GetRetentionPolicy(policy_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit records
   */
  async ListAuditRecords(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await audittrailApi.ListAuditRecords(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit records by actor
   */
  async ListAuditRecordsByActor(actor_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await audittrailApi.ListAuditRecordsByActor(actor_id, params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit records by resource
   */
  async ListAuditRecordsByResource(resource_urn: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await audittrailApi.ListAuditRecordsByResource(resource_urn, params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List compliance reports
   */
  async ListComplianceReports(params?: PaginatedRequest): Promise<ListComplianceReportsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ListComplianceReports(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List data subject access requests
   */
  async ListDataSubjectAccessRequests(params?: PaginatedRequest): Promise<ListDataSubjectAccessRequestsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ListDataSubjectAccessRequests(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List retention policies
   */
  async ListRetentionPolicies(params?: PaginatedRequest): Promise<ListRetentionPoliciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ListRetentionPolicies(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Process data subject access request
   */
  async ProcessDataSubjectAccessRequest(request_id: string, data: ProcessDataSubjectAccessRequestRequest): Promise<DataSubjectAccessRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.ProcessDataSubjectAccessRequest(request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Record an audit event
   */
  async RecordAuditEvent(data: RecordAuditEventRequest): Promise<AuditRecordResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.RecordAuditEvent(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search audit records
   */
  async SearchAuditRecords(data: SearchAuditRecordsRequest): Promise<ListAuditRecordsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.SearchAuditRecords(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update retention policy
   */
  async UpdateRetentionPolicy(policy_id: string, data: UpdateRetentionPolicyRequest): Promise<RetentionPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.UpdateRetentionPolicy(policy_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify chain integrity
   */
  async VerifyChainIntegrity(chain_id: string, data: VerifyChainIntegrityRequest): Promise<VerifyChainIntegrityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await audittrailApi.VerifyChainIntegrity(chain_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const audittrailStore = new AudittrailStore();
